import { nanoid } from 'nanoid'

/**
 * Chat ids this browser minted for an empty "New chat" placeholder.
 *
 * A placeholder has no database row until its thread's first POST, and the
 * tab loses its `isNew` flag the moment the chat is adopted (the full-page
 * hop promotes it in `adoptTabInto`). The chat panel's access gate therefore
 * cannot read the tab list to tell "a chat this device just started" from "a
 * chat this member may not read" — it asks here instead.
 *
 * In memory on purpose: a reload starts a new session with no ids, which is
 * the fail-closed answer for anything that was never written. The launcher
 * hand-off keeps its own reload-safe proof (`hasPendingPrompt`).
 */
const MAX_TRACKED_IDS = 50

/** Where an id was minted: the proof counts only in that org and brand. */
export type ChatMintScope = { orgId: string; brandId?: string }

const mintedChatIds = new Map<string, string>()
const listeners = new Set<() => void>()

function scopeKey(scope: ChatMintScope): string {
  return `${scope.orgId}\u0000${scope.brandId ?? ''}`
}

function emitChange(): void {
  // Deferred: the tab seed mints its id during `useCanvasChats`'s render, and
  // notifying a subscriber synchronously there would schedule an update on a
  // different component mid-render. Readers take the value from the snapshot,
  // so the microtask only wakes a panel that is already mounted.
  queueMicrotask(() => {
    for (const listener of listeners) {
      listener()
    }
  })
}

/** Record `chatId` as locally minted in `scope` (for the mint sites + tests). */
export function rememberLocalChatId(
  chatId: string,
  scope: ChatMintScope
): void {
  // Server renders mint too (the tab seed runs during render); only the
  // browser's own mints are proof, and a server module must not accumulate.
  if (typeof window === 'undefined' || !chatId || mintedChatIds.has(chatId)) {
    return
  }
  mintedChatIds.set(chatId, scopeKey(scope))
  // Bounded: the strip caps open tabs, so older ids can never be the thread.
  while (mintedChatIds.size > MAX_TRACKED_IDS) {
    const oldest = mintedChatIds.keys().next().value
    if (oldest === undefined) {
      break
    }
    mintedChatIds.delete(oldest)
  }
  emitChange()
}

/** Mint a chat id for a local placeholder tab in `scope` and remember it. */
export function mintChatId(scope: ChatMintScope): string {
  const chatId = nanoid()
  rememberLocalChatId(chatId, scope)
  return chatId
}

/**
 * Proof only in the org and brand the id was minted in: a placeholder carried
 * across a brand or org switch is not this scope's chat to create.
 */
export function isLocallyMintedChatId(
  chatId: string | null,
  scope: ChatMintScope
): boolean {
  return Boolean(chatId && mintedChatIds.get(chatId) === scopeKey(scope))
}

/** `useSyncExternalStore` subscription — fires when an id is remembered. */
export function subscribeLocalChatIds(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
